import React, { useState, useEffect } from 'react';

const FingerprintCaptureModal = ({ isOpen, onClose, onCapture }) => {
    const [status, setStatus] = useState('waiting'); // waiting, scanning, success, error
    const [progress, setProgress] = useState(0);
    const [scannedImage, setScannedImage] = useState(null);

    useEffect(() => {
        if (isOpen) {
            resetScanner();
        }
    }, [isOpen]);

    const resetScanner = () => {
        setStatus('waiting');
        setProgress(0);
        setScannedImage(null);
    };

    const startScan = () => {
        setStatus('scanning');
        setProgress(0);

        // Simulación de escaneo profesional
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    completeScan();
                    return 100;
                }
                return prev + 2; // Velocidad de escaneo
            });
        }, 30);
    };

    const completeScan = () => {
        setStatus('processing');
        setTimeout(() => {
            // Generar una imagen de huella dactilar simulada (o usar una base64 real si se tuviera)
            // Aquí usamos un placeholder SVG convertido a DataURL para demostración
            const svgFingerprint = `
                <svg xmlns="http://www.w3.org/2000/svg" width="200" height="300" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 6" />
                    <path d="M5 15.7c-.2.9-.8 1.2-1.5 1.2s-1.3-.4-1.5-1.2" />
                    <path d="M8.9 18.7c-.4 1.1-1.3 1.6-2.4 1.6s-2-.5-2.4-1.6" />
                    <path d="M13.8 21.3c-.6 1.3-1.8 1.9-3.3 1.9s-2.7-.6-3.3-1.9" />
                    <path d="M19 15.7c.2.9.8 1.2 1.5 1.2s1.3-.4 1.5-1.2" />
                    <path d="M15.1 18.7c.4 1.1 1.3 1.6 2.4 1.6s2-.5 2.4-1.6" />
                    <path d="M12 5.5v.5" />
                    <path d="M12 9v.5" />
                    <path d="M12 12.5v.5" />
                </svg>
            `;
            const blob = new Blob([svgFingerprint], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);

            // En un caso real, aquí se recibiría la imagen del SDK del lector
            // Como fallback, permitimos subir una imagen si no hay lector
            setScannedImage(url);
            setStatus('success');
        }, 800);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setScannedImage(e.target.result);
                setStatus('success');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleConfirm = () => {
        if (scannedImage) {
            // Convertir blob URL a data URL si es necesario para Fabric.js
            if (scannedImage.startsWith('blob:')) {
                fetch(scannedImage)
                    .then(r => r.blob())
                    .then(blob => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            onCapture(reader.result);
                            onClose();
                        };
                        reader.readAsDataURL(blob);
                    });
            } else {
                onCapture(scannedImage);
                onClose();
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[9999] p-4 font-sans">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 p-8 max-w-md w-full shadow-2xl flex flex-col items-center relative overflow-hidden">

                {/* Efecto de escaneo de fondo */}
                {status === 'scanning' && (
                    <div className="absolute inset-0 bg-green-500/5 animate-pulse pointer-events-none"></div>
                )}

                <h3 className="text-xl font-bold text-white mb-1">Escáner Biométrico</h3>
                <p className="text-slate-400 text-sm mb-6">Coloque el dedo en el lector</p>

                <div className="relative w-48 h-64 bg-black rounded-xl border-2 border-slate-700 flex items-center justify-center mb-6 overflow-hidden shadow-inner">
                    {/* Visualización del estado */}
                    {status === 'waiting' && (
                        <div className="text-slate-600 animate-pulse">
                            <svg className="w-24 h-24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 6" /><path d="M12 12v.01" /><path d="M12 16v.01" /><path d="M12 8v.01" /></svg>
                        </div>
                    )}

                    {status === 'scanning' && (
                        <>
                            <div className="absolute top-0 left-0 w-full h-1 bg-green-500 shadow-[0_0_15px_rgba(34,197,94,0.8)] animate-[scan_1.5s_ease-in-out_infinite]"></div>
                            <svg className="w-24 h-24 text-green-500/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 6" /><path d="M12 12v.01" /></svg>
                        </>
                    )}

                    {status === 'success' && scannedImage && (
                        <img src={scannedImage} alt="Huella" className="w-full h-full object-contain p-4 filter invert" />
                    )}

                    {/* Barra de progreso */}
                    {status === 'scanning' && (
                        <div className="absolute bottom-0 left-0 h-1 bg-green-500 transition-all duration-75" style={{ width: `${progress}%` }}></div>
                    )}
                </div>

                <div className="w-full space-y-3">
                    {status === 'waiting' && (
                        <button
                            onClick={startScan}
                            className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold transition-colors shadow-lg shadow-blue-900/30 flex items-center justify-center space-x-2"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.2-2.858.59-4.18" /></svg>
                            <span>Iniciar Escaneo</span>
                        </button>
                    )}

                    {status === 'success' && (
                        <div className="flex space-x-3">
                            <button onClick={resetScanner} className="flex-1 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800">
                                Reintentar
                            </button>
                            <button onClick={handleConfirm} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 font-bold shadow-lg shadow-green-900/30">
                                Aceptar
                            </button>
                        </div>
                    )}

                    <div className="pt-4 border-t border-slate-800 mt-4">
                        <p className="text-xs text-center text-slate-500 mb-2">¿No detecta el lector?</p>
                        <label className="flex items-center justify-center w-full py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg cursor-pointer transition-colors text-sm border border-slate-700 border-dashed">
                            <span>📂 Subir imagen manualmente</span>
                            <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                        </label>
                    </div>

                    <button onClick={onClose} className="w-full py-2 text-slate-500 hover:text-slate-300 text-sm mt-2">
                        Cancelar
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes scan {
                    0%, 100% { top: 0%; opacity: 0; }
                    10% { opacity: 1; }
                    90% { opacity: 1; }
                    50% { top: 100%; }
                }
            `}</style>
        </div>
    );
};

export default FingerprintCaptureModal;
